import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";

import "../../../styles/components/dialogs/accounts/manageAccountsDialogComponent.scss";
import "../../../styles/components/dialogs/agents/assignAgentCustomersDialogComponent.scss";
import AlertService from "../../../services/alerts/alert.service";
import agentService from "../../../services/agents/agent.service";
import validationHelper from "../../../helpers/validation.helper";
import SelectInputElementComponent from "../../../components/general/elements/inputElements/SelectInputElementComponent";
import useBusinessCustomerListQuery from "../../../hooks/queries/useBusinessCustomerListQuery";
import useDelayedFlag from "../../../hooks/useDelayedFlag";
import { SkeletonSelect } from "../../../components/general/skeleton";

// 0011: shared business-customer label format, consistent with the create-account dialog.
const customerLabel = (customer) => {
  const name = [customer.firstName, customer.lastName].filter(Boolean).join(" ");
  return name ? `${name} - ${customer.customerId}` : `${customer.customerId}`;
};

// Admin-only screen to set an agent's assigned customers (full replace). The customer list
// is loaded from /BusinessCustomer/List (Administrator-only). An empty selection means the
// agent has NO restriction (may act for any customer).
const AssignAgentCustomersDialogComponent = () => {
  const [searchParams] = useSearchParams();
  const agentId = searchParams.get("id");

  const [selectedIds, setSelectedIds] = useState([]);
  const [assignmentsLoading, setAssignmentsLoading] = useState(true);

  // 0016: the customer list now comes from the shared React Query hook (one deduped fetch of the
  // heavy /BusinessCustomer/List, cached across this and the add-account dialog). The per-agent
  // assignments stay a one-off fetch (itself deduped in the service layer).
  const { data: customers = [], isLoading: customersLoading } = useBusinessCustomerListQuery();

  const fetchAssignments = useCallback(async () => {
    if (!agentId) return;
    setAssignmentsLoading(true);
    try {
      const assignmentsResponse = await agentService.getAgentAssignments(agentId);
      if (validationHelper.validateHttpResponse(assignmentsResponse.data)) {
        setSelectedIds(assignmentsResponse.data.payload || []);
      }
    } catch (error) {
      AlertService.error("טעינת הנתונים נכשלה", "אנא נסה שנית מאוחר יותר");
      console.error("Error loading assignment data:", error);
    } finally {
      setAssignmentsLoading(false);
    }
  }, [agentId]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const isLoading = customersLoading || assignmentsLoading;

  // 0018: gate the skeleton on a ~120ms delay so a cached customer list (5-min staleTime)
  // plus an already-resolved assignment fetch shows the multi-select instantly, no flash.
  const showSkeleton = useDelayedFlag(isLoading);

  const handleSave = async () => {
    try {
      const response = await agentService.setAgentAssignments(agentId, selectedIds);
      if (!validationHelper.validateHttpResponse(response.data)) {
        AlertService.error("שמירת השיוך נכשלה", "אנא נסה שנית מאוחר יותר");
        return;
      }
      AlertService.success("שיוך הלקוחות נשמר בהצלחה");
    } catch (error) {
      AlertService.error("שמירת השיוך נכשלה", "אנא נסה שנית מאוחר יותר");
      console.error("Error saving assignments:", error);
    }
  };

  const getCustomerSelectElement = () => {
    const inputProps = {
      name: "assignedCustomerIds",
      selectionLabel: "לקוחות משויכים",
      value: selectedIds,
      options: customers.map((customer) => ({ value: customer.customerId, label: customerLabel(customer) })),
      onChange: setSelectedIds,
      multiple: true,
    };
    return <SelectInputElementComponent props={inputProps} />;
  };

  // 0012: fixed-height three-region layout — pinned header + scrollable body + pinned
  // footer — so the chips can never push the title or the save button out of view.
  return (
    <div className="dialogContainer manageAccountsDialogContainer assignAgentCustomersDialogContainer">
      <div className="assignDialogHeader">
        <h1>שיוך לקוחות לסוכן</h1>
        <p>בחר את הלקוחות שהסוכן רשאי לפעול עבורם. ללא בחירה — הסוכן רשאי לפעול עבור כל הלקוחות.</p>
      </div>

      <div className="assignDialogBody">{isLoading ? showSkeleton && <SkeletonSelect multi chips={6} /> : getCustomerSelectElement()}</div>

      <div className="newAccountContainer">
        <button className="addAccountButton primaryButton" onClick={handleSave}>
          שמירת שיוך ({selectedIds.length})
        </button>
        <Link to="?d=agent&action=manage" className="addAccountButton">
          חזרה לרשימת הסוכנים
        </Link>
      </div>
    </div>
  );
};

export default AssignAgentCustomersDialogComponent;
